import AsyncBoundary from "@components/async-boundary";
import HeaderWeb from "@components/header-web";
import useShowRecipes from "@hooks/use-show-recipes";
import { Provider } from "@provider/index";
import { SafeArea } from "@provider/safe-area";
import SentryLoader from "@provider/sentry-loader";
import { useDripsyTheme } from "dripsy";
import * as React from "react";
import { Platform } from "react-native";
import { createBottomTabsRoot, createSimpleTheme, NavigationProvider } from "react-native-ridge-navigation";

import { BottomRoots, screens } from "./Navigator";
import { NavigationRoots } from "./NavigatorRoots";
import CartBadgeUpdater from "@features/cart/cart-badge-updater";
import CanonicalLink from "@components/canonical-link";
import UpdatePWA from "@features/pwa/update-pwa";
import CustomStatusBar from "@components/custom-status-bar.native";
import AssistantPanel, { AssistantFloatingButton } from "@features/assistant/assistant-panel";
import { useAssistant } from "@features/assistant/assistant-provider";
import { useAssistantEnabled } from "@features/assistant/use-assistant-enabled";
import installWebHistoryGuard from "@components/history-guard";

// Web: voorkom "The Url / is invalid" bij terug naar de eerste history-entry
// ("/" of een kapotte deep link). Standaard-URL = eerste tab van RootApp.
installWebHistoryGuard(`/${NavigationRoots.RootApp}${BottomRoots.Home.path}`);

type SimpleTheme = Parameters<typeof createSimpleTheme>[0]["light"];

function App() {
  return (
    <>
      <SentryLoader />
      <CustomStatusBar />
      <Provider>
        <UpdatePWA />
        <Navigation />
      </Provider>
    </>
  );
}

function Navigation() {
  const showRecipes = useShowRecipes();
  const {
    theme: { colors },
  } = useDripsyTheme();

  const baseTheme: SimpleTheme = React.useMemo(
    () => ({
      primary: colors.$primary,
      accent: colors.$primary,
      text: colors.$text,
      backgroundColor: colors.$background,
      bottomTabs: {
        fontFamily: "DM Sans",
        textColor: colors.$lightGreenOpacity,
        activeIndicatorColor: colors.$lightGreenOpacity12,
        rippleColor: colors.$lightGreenOpacity12,
        // selectedTextColor: dr.theme.colors.$primary,

        backgroundColor: colors.$secondaryBackground,
      },
    }),
    [colors],
  );
  const themeSettings = React.useMemo(
    () =>
      createSimpleTheme({
        light: baseTheme,
        dark: baseTheme,
      }),
    [baseTheme],
  );
  const navigationRoot = React.useMemo(() => {
    return {
      [NavigationRoots.RootApp]: createBottomTabsRoot(
        Platform.OS === "web"
          ? [
              BottomRoots.Home,
              showRecipes && BottomRoots.Recipes,
              BottomRoots.Products,
              BottomRoots.Cart,
              BottomRoots.Account,
              BottomRoots.Contact,
              BottomRoots.Webshop,
              BottomRoots.AboutUs,
            ].filter(Boolean)
          : [
              BottomRoots.Home,
              BottomRoots.Products,
              showRecipes && BottomRoots.Recipes,
              BottomRoots.Cart,
              BottomRoots.Account,
            ].filter(Boolean),
        {
          components: {
            override: HeaderWeb,
          },
        },
      ),
    };
  }, [showRecipes]);
  return (
    <NavigationProvider
      // Geen basePath: de web-app staat sinds de domeinverhuizing op de root
      // van degroteverleiding.nl (/app/...), net als in development.
      key={`${showRecipes ? "showRecipes" : "hideRecipes"}`}
      screens={screens}
      SuspenseContainer={SuspenseContainer}
      navigationRoot={navigationRoot}
      themeSettings={themeSettings}
      initialRootKey={NavigationRoots.RootApp}
    >
      <CartBadgeUpdater />
      <CanonicalLink />
      <AssistantShell />
    </NavigationProvider>
  );
}

function AssistantShell() {
  const isAssistantEnabled = useAssistantEnabled();
  const { isOpen, close } = useAssistant();

  React.useEffect(() => {
    if (!isAssistantEnabled && isOpen) {
      close();
    }
  }, [close, isAssistantEnabled, isOpen]);

  if (!isAssistantEnabled) {
    return null;
  }

  return (
    <>
      <AssistantFloatingButton />
      <AssistantPanel />
    </>
  );
}

const SuspenseContainer = ({ children }: { children: any }) => {
  return (
    <SafeArea>
      <AsyncBoundary>{children}</AsyncBoundary>
    </SafeArea>
  );
};
export default App;
